/**
 * AuthProvider — React context wrapping Supabase auth state.
 *
 * Responsibilities:
 *   - Load the current session at mount (fast, from localStorage).
 *   - Subscribe to onAuthStateChange for real-time updates
 *     (sign-in, sign-out, token refresh, password recovery).
 *   - Expose { user, session, loading, signIn, signUp, signOut }.
 *
 * Auth-gated routes read from useAuth() and render nothing / redirect
 * while `loading` is true, so we never briefly flash unauthed content.
 */

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // 1. Load session synchronously from local storage.
        supabase.auth.getSession().then(({ data }) => {
            setSession(data?.session ?? null);
            setLoading(false);
        });

        // 2. Subscribe for future changes.
        const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
            setSession(s ?? null);
            // Note: we intentionally do NOT touch `loading` here — it's
            // one-shot at mount time.
        });
        return () => sub.subscription.unsubscribe();
    }, []);

    const value = useMemo(
        () => ({
            session,
            user: session?.user ?? null,
            loading,
            signIn: async (email, password) => {
                const { data, error } = await supabase.auth.signInWithPassword({
                    email, password,
                });
                if (error) throw error;
                return data;
            },
            signUp: async (email, password, displayName) => {
                const { data, error } = await supabase.auth.signUp({
                    email, password,
                    options: {
                        data: { display_name: displayName || undefined },
                    },
                });
                if (error) throw error;
                return data;
            },
            signOut: async () => {
                const { error } = await supabase.auth.signOut();
                if (error) throw error;
            },
        }),
        [session, loading],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
    return ctx;
}
